import React from 'react';
import { CareflowCollaborationNote, CareflowCollaborationAuditEntry } from './api/careflow360Api';
import { useI18n } from '../../i18n/I18nProvider';

interface CareFlow360CollaborationProps {
  collabNotes: CareflowCollaborationNote[];
  collabAudit: CareflowCollaborationAuditEntry[];
  newCollabBody: string;
  setNewCollabBody: (v: string) => void;
  newCollabMentions: string;
  setNewCollabMentions: (v: string) => void;
  collabSaving: boolean;
  handleCreateCollaborationNote: () => Promise<void>;
  handleDeleteCollaborationNote: (noteId: number) => Promise<void>;
  formatDateTime: (iso?: string | null) => string;
}

export default function CareFlow360Collaboration({
  collabNotes,
  collabAudit,
  newCollabBody,
  setNewCollabBody,
  newCollabMentions,
  setNewCollabMentions,
  collabSaving,
  handleCreateCollaborationNote,
  handleDeleteCollaborationNote,
  formatDateTime,
}: CareFlow360CollaborationProps) {
  const { t } = useI18n();

  return (
    <div className="cf360-collaboration-view">
      <h3>💬 {t('careflow_collab_notes')}</h3>
      
      <div className="cf360-collab-compose">
        <textarea
          value={newCollabBody}
          onChange={(e) => setNewCollabBody(e.target.value)}
          placeholder={t('careflow_collab_note_placeholder')}
        />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            style={{ flex: 1 }}
            value={newCollabMentions}
            onChange={(e) => setNewCollabMentions(e.target.value)}
            placeholder={t('careflow_collab_mentions_placeholder')}
          />
          <button
            type="button"
            className="cf360-small-btn"
            onClick={() => void handleCreateCollaborationNote()}
            disabled={collabSaving}
            style={{ whiteSpace: 'nowrap' }}
          >
            {collabSaving ? `⏳ ${t('careflow_add_note_saving')}` : `📝 ${t('careflow_add_note')}`}
          </button>
        </div>
      </div>

      <ul className="cf360-collab-list">
        {collabNotes.map((note) => (
          <li key={`note-${note.id}`} className="cf360-collab-item">
            <div className="cf360-collab-top">
              <span className="cf360-collab-meta">
                #{note.id} v{note.note_version}
                {note.author_role ? ` • ${note.author_role}` : ''}
              </span>
              <span className="audit-time">🕒 {formatDateTime(note.updated_at || note.created_at)}</span>
            </div>
            <div className="cf360-collab-body">{note.body}</div>
            {note.mentions.length > 0 && (
              <div className="cf360-collab-mentions">
                📌 {t('careflow_mentions_label')}: {note.mentions.join(', ')}
              </div>
            )}
            <div style={{ marginTop: '12px', textAlign: 'right' }}>
              <button
                type="button"
                className="cf360-link-btn"
                onClick={() => void handleDeleteCollaborationNote(note.id)}
              >
                🗑️ {t('careflow_delete')}
              </button>
            </div>
          </li>
        ))}
        {collabNotes.length === 0 && (
          <li className="cf360-muted-item">{t('careflow_collab_none')}</li>
        )}
      </ul>

      {collabAudit.length > 0 && (
        <>
          <h4 className="events-group-title mt-4">🕵️ {t('careflow_collab_audit')}</h4>
          <ul className="cf360-audit-list">
            {collabAudit.map((entry) => (
              <li key={`collab-audit-${entry.id}`} className="audit-item">
                <div className="audit-row-top">
                  <span className="audit-action">{entry.action.toUpperCase()}</span>
                  <span className="audit-time">{formatDateTime(entry.created_at)}</span>
                </div>
                <div className="audit-row-main">
                  {t('careflow_note_prefix')} <strong>#{entry.note_id ?? '—'}</strong>
                  {entry.actor_role ? ` • ${entry.actor_role}` : ''}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
